import React, { RefObject, forwardRef, memo, useEffect, useMemo, useState } from 'react';
import { DATE_FORMAT, getDateWithCorrectTime, getDaysOfInterval, getWeekDayIndex, isNonWorkingDay, months, setClass, useViewModel, weekDaysShort } from '@utils';
import { format, getMonth, getYear } from 'date-fns';
import { toast } from 'react-toastify';
import { GrayArrowLeft, GrayArrowRight } from '../svgs';
import { CalendarViewModel } from '../../views';
import { observer } from 'mobx-react-lite';
import '../../styles/components/calendarPreview.scss';

interface CalendarPreviewProps {
  startDate: Date;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  isNeedMainWorkCalendar?: boolean;
  getDayClassName?: (day: Date) => string;
  onClickDay: (day: Date) => void;
  moveToNextMonth: () => void;
  moveToPrevMonth: () => void;
}

export const CalendarPreview = memo(observer(forwardRef<HTMLDivElement, CalendarPreviewProps>(({ 
  startDate, minDate, maxDate, disablePast, getDayClassName: getClassNameProp, onClickDay, 
  moveToNextMonth, moveToPrevMonth, isNeedMainWorkCalendar = true, 
}, ref: RefObject<HTMLDivElement>) => {
  const calendarViewModel = useViewModel(CalendarViewModel);
  const [workCalendar, setWorkCalendar] = useState([]);
  useEffect(() => {
    !isNeedMainWorkCalendar && calendarViewModel.getWorkCalendarFromDateMonth(startDate).then((data) => setWorkCalendar(data));
  }, [startDate])

  const today = calendarViewModel.today;

  const isDisabled = (item: Date) =>
    (disablePast && (item.getTime() < (new Date(today.getFullYear(), today.getMonth(), today.getDate())).getTime()))
    || item.getTime() < minDate?.getTime()
    || item > maxDate;

  const getClassName = (item: Date) => {
    const currentMonthClassName = getMonth(startDate) === getMonth(item) && 'current-month';
    const todayClassName = format(today, DATE_FORMAT) === format(item, DATE_FORMAT) && 'today';
    const disabled = isDisabled(item) && 'disabled';
    const findCalendarDay = isNeedMainWorkCalendar 
      ? calendarViewModel.findWorkCalendarDay(item) 
      : calendarViewModel.findWorkCalendarDay(item, workCalendar);
    const isHolidayClassName = isNonWorkingDay(getWeekDayIndex(item), findCalendarDay);

    return setClass('day', currentMonthClassName, todayClassName, disabled, getClassNameProp?.(item), isHolidayClassName && 'holiday');
  };
  const errorMessage = useMemo(() => {
    if (minDate) return `Дата окончания не может быть раньше ${minDate.toLocaleDateString()}`;
    else if (maxDate) return `Дата начала не может быть позже ${maxDate.toLocaleDateString()}`;
    else if (disablePast) return `Вы можете выбрать дату, начиная от сегодняшнего дня`;
    else return 'Выбранная дата не может быть установлена';
  }, [minDate, maxDate, disablePast]);

  const onClick = (day: Date) =>
    !isDisabled(day) ? onClickDay(day) : toast.error(errorMessage);

  const dates = useMemo(() => getDaysOfInterval(startDate, 'month'), [startDate]);

  return (
    <div
      ref={ref}
      className='block calendar-preview'
    >
      <div className="month-bar">
        {`${months[getMonth(startDate)]} ${getYear(startDate)}`}
        <div className="change-month-buttons">
          <span
            className='change-month-button'
            onClick={moveToPrevMonth}
          >
            <GrayArrowLeft />
          </span>
          <span
            className='change-month-button'
            onClick={moveToNextMonth}
          >
            <GrayArrowRight />
          </span>
        </div>
      </div>
      <div className='week-days'>
        {weekDaysShort.map((day: string) => (
          <span
            key={day}
            className='day'
          >
            {day}
          </span>
        ))}
      </div>
      <div className='schedule'>
        {dates.map((item: Date, index: number) => {
          const day = getDateWithCorrectTime(item, startDate.getHours(), startDate.getMinutes());
          return (
              <span
                key={day.toLocaleDateString() + index}
                className={getClassName(day)}
                onClick={() => onClick(day)}
              >
                {day.getDate()}
              </span>
            );
        })}
      </div>
    </div>
  );
})));